import { useEffect, type RefObject } from 'react';

/**
 * Dismissal behaviour shared by every disclosure in the shell (V2-018C-1).
 *
 * The legacy navigation disclosure is mouse-oriented: it closes on an outside
 * click and offers no keyboard path out. Specification section 3.1 makes
 * keyboard dismissal a completion condition rather than an enhancement, so both
 * paths live here and every disclosure gets them by construction.
 *
 * Escape restores focus to the trigger. Without that, dismissing with the
 * keyboard drops focus to the document body and the visitor has to tab from the
 * top of the page again.
 */
export function useDisclosure(
  open: boolean,
  close: () => void,
  containerRef: RefObject<HTMLElement | null>,
  triggerRef: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent): void => {
      const target = event.target as Node | null;
      if (!target) return;
      if (containerRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      close();
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      close();
      triggerRef.current?.focus();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close, containerRef, triggerRef]);
}

/** Elements that can hold focus inside a trapped panel. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Focus containment for the mobile navigation panel (specification 3.2).
 *
 * A full-screen panel that does not trap focus lets a Tab press walk into the
 * page behind it, which for a screen-reader or keyboard visitor reads as the
 * panel having closed when it has not. Focus moves to the first focusable
 * element on open and wraps at both ends.
 */
export function useFocusTrap(open: boolean, containerRef: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    if (!open) return;
    const container = containerRef.current;
    if (!container) return;

    // Spread rather than `Array.from`: the browser-boundary guard in
    // test/noBrowserDbAccess.test.ts bars `.from(` anywhere in browser source,
    // because that is how a Supabase table read is written.
    const focusable = (): HTMLElement[] => [
      ...container.querySelectorAll<HTMLElement>(FOCUSABLE),
    ];

    focusable()[0]?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') return;
      const elements = focusable();
      if (elements.length === 0) return;

      const first = elements[0]!;
      const last = elements[elements.length - 1]!;
      const active = document.activeElement;

      // Focus that is already outside the panel is the case the boundary checks
      // below cannot see: neither `active === first` nor `active === last`
      // matches, nothing is prevented, and Tab walks into the page behind an
      // open menu. Reclaim it before testing the boundaries.
      if (!active || !container.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
        return;
      }
      if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, containerRef]);
}

/**
 * Hold the page still while a full-screen panel is open (specification 3.2).
 *
 * Scrolling the document behind an open panel is disorienting on touch, and on
 * return the visitor is somewhere they did not navigate to.
 */
export function useScrollLock(locked: boolean): void {
  useEffect(() => {
    if (!locked) return;
    document.body.classList.add('overflow-hidden');
    return () => document.body.classList.remove('overflow-hidden');
  }, [locked]);
}
